import { VStack, Span, HStack } from "@chakra-ui/react"
import * as gqlTypes from "@hydrologiq/gql/graphql"
import { useMemo, useEffect, useRef, useCallback, useState } from "react"
import SearchCombobox from "./SearchCombobox"
import BatchCreateButton from "./BatchCreate"
import { useQueryWithEndpoint } from "@hydrologiq/shared/hooks/useGraphQLClient"

export type BatchItem = gqlTypes.Batch

const BatchCombobox = (props: {
  onItemSelection?: (value: BatchItem | undefined) => void
  initialValue?: string
  value?: string | null
  onBatchCreated?: (callback: (batch: BatchItem | null, shouldSelect?: boolean) => void) => void
  disabled?: boolean
  layout?: "horizontal" | "vertical"
  refreshTrigger?: number
  required?: boolean
  showCreateButton?: boolean // Whether to show the inline create button (default: true)
  onReady?: (methods: { selectBatch: (batch: BatchItem) => void }) => void // Expose methods to parent
}) => {
  const {
    onItemSelection,
    initialValue,
    value: controlledValue,
    onBatchCreated,
    disabled = false,
    layout = "horizontal",
    refreshTrigger,
    required,
    showCreateButton = true,
    onReady: onReadyProp,
  } = props

  // Use controlled value if provided, otherwise fall back to initialValue
  const effectiveValue = controlledValue !== undefined ? controlledValue : initialValue

  // Track current input value for default batch name
  const [currentInputValue, setCurrentInputValue] = useState("")

  // Ref to store methods from SearchCombobox
  const searchMethodsRef = useRef<{
    addItemToCache: (item: BatchItem) => void
    selectItem: (item: BatchItem) => void
  } | null>(null)

  // Fetch batches using Apollo for initial value lookup (without pagination params returns simple array)
  // Use cache-and-network to ensure we get fresh data while showing cached data immediately
  const { data: batchesData } = useQueryWithEndpoint(
    gqlTypes.GetBatchesDocument,
    {
      fetchPolicy: "cache-and-network",
      errorPolicy: "all",
      skip: !effectiveValue, // Only fetch if we need to find initial value
      variables: {}, // No variables = simple array response for backwards compatibility
    },
    "primary"
  )

  // Find initial batch item if effectiveValue provided
  const initialBatch = useMemo(() => {
    if (!effectiveValue) return undefined
    // Handle both response formats: paginated (BatchesResponse) and simple array
    const batches = (batchesData?.batches?.data || batchesData?.batches || []) as BatchItem[]
    return batches.find((batch) => batch.id === effectiveValue)
  }, [effectiveValue, batchesData])

  const handleBatchCreated = useCallback(
    (newBatch: BatchItem | null, shouldSelect: boolean = true) => {
      if (!newBatch) return

      if (searchMethodsRef.current) {
        // Always add the new batch to the searchable cache
        searchMethodsRef.current.addItemToCache(newBatch)

        // Only select the item if shouldSelect is true (i.e., no previous value)
        if (shouldSelect) {
          searchMethodsRef.current.selectItem(newBatch)

          // Notify parent component only when selecting
          if (onItemSelection) {
            onItemSelection(newBatch)
          }
        }
      }
    },
    [onItemSelection]
  )

  // Expose the handleBatchCreated function to parent component
  useEffect(() => {
    if (onBatchCreated) {
      onBatchCreated(handleBatchCreated)
    }
  }, [onBatchCreated, handleBatchCreated])

  // Expose methods to parent component
  useEffect(() => {
    if (onReadyProp && searchMethodsRef.current) {
      onReadyProp({
        selectBatch: (batch: BatchItem) => {
          searchMethodsRef.current?.addItemToCache(batch)
          searchMethodsRef.current?.selectItem(batch)
          onItemSelection?.(batch)
        },
      })
    }
  }, [onReadyProp, onItemSelection])

  const combobox = (
    <SearchCombobox
      queryType="batches"
      onItemSelection={(item) => {
        onItemSelection?.(item)
        setCurrentInputValue("") // Clear input value when item is selected
      }}
      label="batches"
      value={initialBatch || null}
      loadInitialItems={true} // Load initial items from API
      minSearchLength={2} // Require 2 characters for search
      disabled={disabled}
      refreshTrigger={refreshTrigger}
      required={required}
      onReady={(methods) => {
        searchMethodsRef.current = methods
      }}
      onInputChange={(value) => {
        setCurrentInputValue(value)
      }}
      itemContent={(item) => (
        <VStack gap={0} width="100%">
          <Span width="100%" alignItems="start" textStyle="sm" fontWeight="medium">
            {item.name}
          </Span>
          {item.quantity && (
            <Span width="100%" alignItems="start" textStyle="xs" color="fg.muted">
              Quantity: {item.quantity} kg
            </Span>
          )}
        </VStack>
      )}
    />
  )

  const createButton =
    !disabled && showCreateButton ? (
      <BatchCreateButton
        onBatchCreated={(newBatch) => {
          if (newBatch) {
            handleBatchCreated(newBatch, true) // Auto-select newly created batch
          }
        }}
        defaultName={currentInputValue}
      />
    ) : null

  if (layout === "vertical") {
    return (
      <VStack width="100%" gap={2} align="stretch">
        {combobox}
        {createButton}
      </VStack>
    )
  }

  return (
    <HStack width="100%" gap={2} alignItems="flex-start">
      {combobox}
      {createButton}
    </HStack>
  )
}

export default BatchCombobox
